<template>
  <div class="data-collection">
    <!-- 顶部操作栏 -->
    <div class="header-bar">
      <h3 class="title">
        <i class="el-icon-data-analysis"></i>
        学生学习数据收集
      </h3>
      <div class="actions">
        <el-button type="primary" icon="el-icon-refresh" @click="loadData" :loading="loading">
          刷新数据
        </el-button>
        <el-button type="success" icon="el-icon-download" @click="exportData">
          导出数据
        </el-button>
      </div>
    </div>

    <!-- 数据统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card video">
        <div class="stat-icon">
          <i class="el-icon-video-camera"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ totalVideoTime }}</div>
          <div class="stat-label">总视频观看时长</div>
        </div>
      </div>
      <div class="stat-card quiz">
        <div class="stat-icon">
          <i class="el-icon-edit-outline"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ totalVideoQuiz }}</div>
          <div class="stat-label">随堂练习总题数</div>
        </div>
      </div>
      <div class="stat-card homework">
        <div class="stat-icon">
          <i class="el-icon-document-checked"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ totalHomework }}</div>
          <div class="stat-label">课后练习总题数</div>
        </div>
      </div>
      <div class="stat-card students">
        <div class="stat-icon">
          <i class="el-icon-user"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ studentList.length }}</div>
          <div class="stat-label">学生总数</div>
        </div>
      </div>
    </div>

    <!-- 学生数据表格 -->
    <el-card class="data-table-card">
      <div slot="header" class="card-header">
        <span>学生学习数据明细</span>
        <el-input
          v-model="searchKeyword"
          placeholder="搜索学生姓名"
          prefix-icon="el-icon-search"
          style="width: 200px"
          clearable
        />
      </div>

      <el-table
        :data="filteredStudentList"
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        @row-click="showStudentDetail"
      >
        <el-table-column prop="studentName" label="学生姓名" width="120" fixed>
          <template slot-scope="scope">
            <span class="student-name">{{ scope.row.studentName || scope.row.userName }}</span>
          </template>
        </el-table-column>

        <!-- 视频观看数据 -->
        <el-table-column label="视频观看" align="center">
          <el-table-column prop="videoData.totalDuration" label="观看时长" width="100" align="center">
            <template slot-scope="scope">
              <span class="data-value">{{ formatDuration((scope.row.videoData && scope.row.videoData.totalDuration) || 0) }}</span>
            </template>
          </el-table-column>
          <!-- 完成小节列已隐藏 -->
        </el-table-column>

        <!-- 随堂练习数据 -->
        <el-table-column label="随堂练习" align="center">
          <el-table-column prop="videoQuizData.totalQuestions" label="答题数" width="80" align="center">
            <template slot-scope="scope">
              <span class="data-value">{{ (scope.row.videoQuizData && scope.row.videoQuizData.totalQuestions) || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="videoQuizData.correctCount" label="正确数" width="80" align="center">
            <template slot-scope="scope">
              <span class="data-value correct">{{ (scope.row.videoQuizData && scope.row.videoQuizData.correctCount) || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="videoQuizData.accuracy" label="正确率" width="90" align="center">
            <template slot-scope="scope">
              <el-progress
                :percentage="Math.round((scope.row.videoQuizData && scope.row.videoQuizData.accuracy) || 0)"
                :color="getAccuracyColor(scope.row.videoQuizData && scope.row.videoQuizData.accuracy)"
                :stroke-width="10"
              />
            </template>
          </el-table-column>
          <el-table-column prop="videoQuizData.totalTimeCost" label="答题用时" width="90" align="center">
            <template slot-scope="scope">
              <span class="data-value">{{ formatDuration((scope.row.videoQuizData && scope.row.videoQuizData.totalTimeCost) || 0) }}</span>
            </template>
          </el-table-column>
        </el-table-column>

        <!-- 课后练习数据 -->
        <el-table-column label="课后练习" align="center">
          <el-table-column prop="homeworkData.completedHomeworks" label="完成作业" width="90" align="center">
            <template slot-scope="scope">
              <span class="data-value">
                {{ (scope.row.homeworkData && scope.row.homeworkData.completedHomeworks) || 0 }}/{{ (scope.row.homeworkData && scope.row.homeworkData.totalHomeworks) || 0 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="homeworkData.totalQuestions" label="答题数" width="80" align="center">
            <template slot-scope="scope">
              <span class="data-value">{{ (scope.row.homeworkData && scope.row.homeworkData.totalQuestions) || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="homeworkData.correctCount" label="正确数" width="80" align="center">
            <template slot-scope="scope">
              <span class="data-value correct">{{ (scope.row.homeworkData && scope.row.homeworkData.correctCount) || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="homeworkData.accuracy" label="正确率" width="90" align="center">
            <template slot-scope="scope">
              <el-progress
                :percentage="Math.round((scope.row.homeworkData && scope.row.homeworkData.accuracy) || 0)"
                :color="getAccuracyColor(scope.row.homeworkData && scope.row.homeworkData.accuracy)"
                :stroke-width="10"
              />
            </template>
          </el-table-column>
        </el-table-column>

        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-view" @click.stop="showStudentDetail(scope.row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 学生详情对话框 -->
    <el-dialog
      :title="'学生学习详情 - ' + ((currentStudent && currentStudent.studentName) || (currentStudent && currentStudent.userName) || '')"
      :visible.sync="detailDialogVisible"
      width="900px"
      top="5vh"
    >
      <div v-loading="detailLoading" class="student-detail">
        <el-tabs v-model="activeTab">
          <!-- 视频观看详情 -->
          <el-tab-pane label="视频观看记录" name="video">
            <el-table :data="studentDetail.videoDetails" stripe border size="small">
              <el-table-column prop="chapterTitle" label="章节" width="150" />
              <el-table-column prop="sectionTitle" label="小节" width="150" />
              <el-table-column prop="studyDuration" label="观看时长" width="100" align="center">
                <template slot-scope="scope">
                  {{ formatDuration(scope.row.studyDuration || 0) }}
                </template>
              </el-table-column>
              <el-table-column prop="videoProgress" label="观看进度" width="100" align="center">
                <template slot-scope="scope">
                  {{ formatDuration(scope.row.videoProgress || 0) }}
                </template>
              </el-table-column>
              <el-table-column prop="lastStudyTime" label="最后学习时间" width="160">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.lastStudyTime) }}
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- 视频访问日志 -->
          <el-tab-pane label="访问记录" name="visitLog">
            <el-table :data="studentDetail.videoVisitLogs || []" stripe border size="small">
              <el-table-column label="次数" width="70" align="center">
                <template slot-scope="scope">
                  第{{ getVisitNumber(scope.row, scope.$index) }}次
                </template>
              </el-table-column>
              <el-table-column prop="chapterTitle" label="章节" width="150" />
              <el-table-column prop="sectionTitle" label="小节" width="150" />
              <el-table-column prop="visitTime" label="进入时间" width="160">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.visitTime) }}
                </template>
              </el-table-column>
              <el-table-column prop="exitTime" label="退出时间" width="160">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.exitTime) }}
                </template>
              </el-table-column>
              <el-table-column prop="watchDuration" label="观看时长" width="100" align="center">
                <template slot-scope="scope">
                  {{ formatDuration(scope.row.watchDuration || 0) }}
                </template>
              </el-table-column>
              <el-table-column prop="exitPosition" label="退出位置" width="100" align="center">
                <template slot-scope="scope">
                  {{ formatDuration(scope.row.exitPosition || 0) }}
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- 随堂练习详情 -->
          <el-tab-pane label="随堂练习记录" name="quiz">
            <el-table :data="studentDetail.videoQuizDetails" stripe border size="small">
              <el-table-column prop="sectionTitle" label="所属小节" width="150" />
              <el-table-column prop="questionContent" label="题目内容" show-overflow-tooltip />
              <el-table-column prop="userAnswer" label="学生答案" width="100" align="center">
                <template slot-scope="scope">
                  <span :class="scope.row.isCorrect ? 'answer-correct' : 'answer-wrong'">
                    {{ scope.row.userAnswer }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="correctAnswer" label="正确答案" width="100" align="center" />
              <el-table-column prop="isCorrect" label="结果" width="80" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.isCorrect ? 'success' : 'danger'" size="small">
                    {{ scope.row.isCorrect ? '正确' : '错误' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="answerTimeCost" label="用时" width="80" align="center">
                <template slot-scope="scope">
                  {{ scope.row.answerTimeCost || 0 }}秒
                </template>
              </el-table-column>
              <el-table-column prop="answerTimestamp" label="答题时间" width="160">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.answerTimestamp) }}
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- 课后练习详情 -->
          <el-tab-pane label="课后练习记录" name="homework">
            <div v-for="hw in studentDetail.homeworkDetails" :key="hw.homeworkId" class="homework-item">
              <div class="homework-header">
                <span class="homework-title">{{ hw.homeworkTitle }}</span>
                <span class="homework-stats">
                  正确: {{ hw.correctCount }}/{{ hw.totalQuestions }}
                </span>
              </div>
              <el-table :data="hw.answers" stripe border size="small" v-if="hw.answers && hw.answers.length > 0">
                <el-table-column prop="questionContent" label="题目内容" show-overflow-tooltip />
                <el-table-column prop="userAnswer" label="学生答案" width="100" align="center">
                  <template slot-scope="scope">
                    <span :class="scope.row.isCorrect === 1 ? 'answer-correct' : 'answer-wrong'">
                      {{ scope.row.userAnswer }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="correctAnswer" label="正确答案" width="100" align="center" />
                <el-table-column prop="isCorrect" label="结果" width="80" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.isCorrect === 1 ? 'success' : 'danger'" size="small">
                      {{ scope.row.isCorrect === 1 ? '正确' : '错误' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="knowledge" label="知识点" width="150" show-overflow-tooltip />
                <el-table-column prop="timeCost" label="做题用时" width="90" align="center">
                  <template slot-scope="scope">
                    {{ scope.row.timeCost ? scope.row.timeCost + '秒' : '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="analysisViewDuration" label="查看解析" width="90" align="center">
                  <template slot-scope="scope">
                    {{ scope.row.analysisViewDuration ? scope.row.analysisViewDuration + '秒' : '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="answerTime" label="答题时间" width="160">
                  <template slot-scope="scope">
                    {{ formatTime(scope.row.answerTime) }}
                  </template>
                </el-table-column>
              </el-table>
              <el-empty v-else description="暂无答题记录" :image-size="60" />
            </div>
            <el-empty v-if="!studentDetail.homeworkDetails || studentDetail.homeworkDetails.length === 0"
                      description="暂无作业数据" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseSummary, getStudentDetail, exportCourseData } from '@/api/amcDataCollection'

export default {
  name: 'DataCollection',
  props: {
    courseId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      studentList: [],
      searchKeyword: '',

      // 学生详情
      detailDialogVisible: false,
      detailLoading: false,
      currentStudent: null,
      studentDetail: {
        videoDetails: [],
        videoQuizDetails: [],
        homeworkDetails: []
      },
      activeTab: 'video'
    }
  },
  computed: {
    filteredStudentList() {
      if (!this.searchKeyword) {
        return this.studentList
      }
      const keyword = this.searchKeyword.toLowerCase()
      return this.studentList.filter(s =>
        (s.studentName && s.studentName.toLowerCase().includes(keyword)) ||
        (s.userName && s.userName.toLowerCase().includes(keyword))
      )
    },
    totalVideoTime() {
      const total = this.studentList.reduce((sum, s) => sum + ((s.videoData && s.videoData.totalDuration) || 0), 0)
      return this.formatDuration(total)
    },
    totalVideoQuiz() {
      return this.studentList.reduce((sum, s) => sum + ((s.videoQuizData && s.videoQuizData.totalQuestions) || 0), 0)
    },
    totalHomework() {
      return this.studentList.reduce((sum, s) => sum + ((s.homeworkData && s.homeworkData.totalQuestions) || 0), 0)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await getCourseSummary(this.courseId)
        if (res.code === 200 || res.code === 1) {
          this.studentList = res.data || []
        } else {
          this.$message.error(res.msg || '加载数据失败')
        }
      } catch (e) {
        console.error('加载数据失败', e)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    async showStudentDetail(row) {
      this.currentStudent = row
      this.detailDialogVisible = true
      this.detailLoading = true
      this.activeTab = 'video'

      // 重置详情数据
      this.studentDetail = {
        videoDetails: [],
        videoQuizDetails: [],
        homeworkDetails: []
      }

      try {
        const res = await getStudentDetail(row.studentId, this.courseId)
        console.log('学生详情返回数据:', res)
        if (res.code === 200 || res.code === 1) {
          // 使用$set确保响应式更新
          this.$set(this, 'studentDetail', res.data || {
            videoDetails: [],
            videoQuizDetails: [],
            homeworkDetails: []
          })
          console.log('设置后的studentDetail:', this.studentDetail)
        } else {
          this.$message.error(res.msg || '加载详情失败')
        }
      } catch (e) {
        console.error('加载详情失败', e)
        this.$message.error('加载详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async exportData() {
      try {
        const res = await exportCourseData(this.courseId)
        if (res.code === 200 || res.code === 1) {
          // 转换为CSV格式并下载
          this.downloadCSV(res.data)
        } else {
          this.$message.error(res.msg || '导出失败')
        }
      } catch (e) {
        console.error('导出失败', e)
        this.$message.error('导出失败')
      }
    },

    downloadCSV(data) {
      // 构建CSV内容
      const headers = [
        '学生姓名', '用户名',
        '视频观看时长(秒)',
        '随堂练习题数', '随堂正确数', '随堂正确率(%)', '随堂答题用时(秒)',
        '完成作业数', '总作业数', '课后练习题数', '课后正确数', '课后正确率(%)'
      ]

      const rows = data.map(s => [
        s.studentName || '',
        s.userName || '',
        (s.videoData && s.videoData.totalDuration) || 0,
        (s.videoQuizData && s.videoQuizData.totalQuestions) || 0,
        (s.videoQuizData && s.videoQuizData.correctCount) || 0,
        ((s.videoQuizData && s.videoQuizData.accuracy) || 0).toFixed(2),
        (s.videoQuizData && s.videoQuizData.totalTimeCost) || 0,
        (s.homeworkData && s.homeworkData.completedHomeworks) || 0,
        (s.homeworkData && s.homeworkData.totalHomeworks) || 0,
        (s.homeworkData && s.homeworkData.totalQuestions) || 0,
        (s.homeworkData && s.homeworkData.correctCount) || 0,
        ((s.homeworkData && s.homeworkData.accuracy) || 0).toFixed(2)
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n')

      // 添加BOM以支持中文
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `学生学习数据_${new Date().toLocaleDateString()}.csv`
      link.click()
      URL.revokeObjectURL(url)

      this.$message.success('导出成功')
    },

    formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0秒'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60

      if (hours > 0) {
        return `${hours}时${minutes}分${secs}秒`
      } else if (minutes > 0) {
        return `${minutes}分${secs}秒`
      } else {
        return `${secs}秒`
      }
    },

    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString()
    },

    getAccuracyColor(accuracy) {
      if (accuracy >= 80) return '#67C23A'
      if (accuracy >= 60) return '#E6A23C'
      return '#F56C6C'
    },

    // 计算该学生对同一小节的第几次访问
    getVisitNumber(row, index) {
      const logs = this.studentDetail.videoVisitLogs || []
      // 找出同一小节的所有访问记录，按时间排序
      const sameSectionLogs = logs
        .filter(log => log.sectionId === row.sectionId)
        .sort((a, b) => new Date(a.visitTime) - new Date(b.visitTime))
      // 找到当前记录在同一小节中的序号
      const visitIndex = sameSectionLogs.findIndex(log => log.id === row.id)
      return visitIndex + 1
    }
  }
}
</script>

<style scoped>
.data-collection {
  padding: 20px;
}

.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.title i {
  margin-right: 8px;
  color: #409EFF;
}

/* 统计卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-card.video {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-card.quiz {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-card.homework {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card.students {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 28px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-top: 5px;
}

/* 数据表格卡片 */
.data-table-card {
  border-radius: 12px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.student-name {
  font-weight: 500;
  color: #409EFF;
  cursor: pointer;
}

.data-value {
  font-weight: 500;
}

.data-value.correct {
  color: #67C23A;
}

/* 学生详情 */
.student-detail {
  min-height: 400px;
}

.homework-item {
  margin-bottom: 20px;
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  overflow: hidden;
}

.homework-header {
  background: #f5f7fa;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.homework-title {
  font-weight: 600;
  color: #303133;
}

.homework-stats {
  color: #67C23A;
  font-weight: 500;
}

.answer-correct {
  color: #67C23A;
  font-weight: 600;
}

.answer-wrong {
  color: #F56C6C;
  font-weight: 600;
}

/* 响应式 */
@media (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }

  .header-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
